import React, { useMemo, useRef, useState } from "react";
import { collapsedGroupsState, groupsState, listsState, uiState } from "@/states.ts";
import { useSnapshot } from "valtio/react";
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarMenu,
} from "@/components/ui/sidebar.tsx";
import { ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon } from "lucide-react";
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	DragOverEvent,
	DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	TouchSensor,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SidebarListView } from "@/views/lists/SidebarListView.tsx";
import { SortEntry } from "@/api/api.ts";
import { deleteGroup, updateListsSort } from "@/api/offline.ts";
import { processQueue } from "@/api/queue.ts";
import { TodoList, TodoListGroup } from "@/api/types";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useCurrentList, useSortedLists } from "@/hooks/lists.ts";
import { AddMenu } from "@/blocks/AddMenu.tsx";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";

const UNGROUPED = "ungrouped";

const Container: React.FC<{ id: string; empty: boolean; children: React.ReactNode }> = ({
	id,
	empty,
	children,
}) => {
	const { setNodeRef } = useDroppable({ id });
	return (
		<SidebarMenu ref={setNodeRef} className={empty ? "min-h-8" : undefined}>
			{children}
		</SidebarMenu>
	);
};

const GroupHeader: React.FC<{
	group: TodoListGroup;
	collapsed: boolean;
	onToggle: () => void;
	onEdit: () => void;
	onDelete: () => void;
}> = ({ group, collapsed, onToggle, onEdit, onDelete }) => {
	return (
		<div className={"flex items-center gap-1 px-2 py-1"}>
			<button
				type={"button"}
				onClick={onToggle}
				className={
					"flex flex-1 min-w-0 items-center gap-1 text-sidebar-foreground/70 hover:text-sidebar-foreground"
				}
			>
				{collapsed ? (
					<ChevronRightIcon size={16} />
				) : (
					<ChevronDownIcon size={16} />
				)}
				<span className={"truncate text-sm font-medium"}>
					{group.title}
				</span>
			</button>
			<DropdownMenu>
				<DropdownMenuTrigger>
					<EllipsisVerticalIcon size={16} />
				</DropdownMenuTrigger>
				<DropdownMenuContent align={"start"}>
					<DropdownMenuItem onClick={onEdit}>
						Rename
					</DropdownMenuItem>
					<DropdownMenuItem onClick={onDelete}>
						Delete
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

const findContainer = (map: Record<string, string[]>, id: string): string | undefined => {
	if (id in map) return id;
	return Object.keys(map).find((key) => map[key].includes(id));
};

const SidebarLists: React.FC = () => {
	const { lists } = useSnapshot(listsState);
	const { groups } = useSnapshot(groupsState);
	const { groups: collapsed } = useSnapshot(collapsedGroupsState);
	const currentList = useCurrentList(false);
	const isMobile = useIsMobile();
	const didDragRef = useRef(false);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 8 },
		}),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 500, tolerance: 8 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const sortedLists = useSortedLists(lists as TodoList[]);
	const sortedGroups = useMemo(
		() =>
			[...groups].sort(
				(a, b) =>
					new Date(a.created_at).getTime() -
					new Date(b.created_at).getTime(),
			),
		[groups],
	);

	const listsById = useMemo(
		() => new Map(sortedLists.map((l) => [l.id, l])),
		[sortedLists],
	);

	const containerItems = useMemo(() => {
		const map: Record<string, string[]> = { [UNGROUPED]: [] };
		sortedGroups.forEach((g) => {
			map[g.id] = [];
		});
		sortedLists.forEach((l) => {
			const containerId =
				l.group_id && map[l.group_id] ? l.group_id : UNGROUPED;
			map[containerId].push(l.id);
		});
		return map;
	}, [sortedGroups, sortedLists]);

	const [dragItems, setDragItems] = useState<Record<string, string[]> | null>(null);
	const items = dragItems ?? containerItems;

	const handleDragStart = (_event: DragStartEvent) => {
		didDragRef.current = true;
		setDragItems(containerItems);
	};

	const handleDragOver = (event: DragOverEvent) => {
		const { active, over } = event;
		if (!over) return;

		const activeContainer = findContainer(items, String(active.id));
		const overContainer = findContainer(items, String(over.id));
		if (
			!activeContainer ||
			!overContainer ||
			activeContainer === overContainer
		) {
			return;
		}

		setDragItems((prev) => {
			const current = prev ?? containerItems;
			const activeItems = current[activeContainer] ?? [];
			const overItems = current[overContainer] ?? [];
			const overIndex = overItems.indexOf(String(over.id));

			let newIndex: number;
			if (String(over.id) in current) {
				newIndex = overItems.length;
			} else {
				const translated = active.rect.current?.translated;
				const isBelow =
					!!translated &&
					translated.top > over.rect.top + over.rect.height;
				newIndex =
					overIndex >= 0
						? overIndex + (isBelow ? 1 : 0)
						: overItems.length;
			}

			return {
				...current,
				[activeContainer]: activeItems.filter(
					(id) => id !== String(active.id),
				),
				[overContainer]: [
					...overItems.slice(0, newIndex),
					String(active.id),
					...overItems.slice(newIndex),
				],
			};
		});
	};

	const handleDragEnd = (event: DragEndEvent) => {
		didDragRef.current = true;
		const { active, over } = event;
		const current = dragItems ?? containerItems;
		setDragItems(null);

		if (!over) return;

		const activeId = String(active.id);
		const overId = String(over.id);
		const activeContainer = findContainer(current, activeId);
		const overContainer = findContainer(current, overId) ?? activeContainer;
		if (!activeContainer || !overContainer) return;

		const finalMap: Record<string, string[]> = {
			...current,
			[overContainer]: [...(current[overContainer] ?? [])],
		};

		if (activeContainer !== overContainer) {
			finalMap[activeContainer] = (current[activeContainer] ?? []).filter(
				(id) => id !== activeId,
			);
			const target = finalMap[overContainer];
			const overIndex = target.indexOf(overId);
			const insertAt = overIndex >= 0 ? overIndex : target.length;
			target.splice(insertAt, 0, activeId);
		} else {
			const arr = finalMap[overContainer];
			const oldIndex = arr.indexOf(activeId);
			const newIndex = arr.indexOf(overId);
			if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
				finalMap[overContainer] = arrayMove(arr, oldIndex, newIndex);
			}
		}

		const entries: SortEntry[] = [];
		Object.entries(finalMap).forEach(([containerId, ids]) => {
			ids.forEach((id, index) => {
				const list = listsById.get(id);
				// ponytail: shared lists are owned by someone else, the server ignores them in sort
				if (!list || list.is_shared) return;
				entries.push({
					id,
					order: index,
					group_id: containerId === UNGROUPED ? null : containerId,
				});
			});
		});

		const byId = new Map(entries.map((e) => [e.id, e]));
		listsState.lists = listsState.lists.map((l) => {
			const entry = byId.get(l.id);
			return entry ? { ...l, order: entry.order, group_id: entry.group_id } : l;
		});
		updateListsSort(entries);
		processQueue();
	};

	const handleDeleteGroup = (group: TodoListGroup) => {
		deleteGroup(group).then(() => {
			groupsState.groups = groupsState.groups.filter(
				(g) => g.id !== group.id,
			);
			listsState.lists = listsState.lists.map((l) =>
				l.group_id === group.id ? { ...l, group_id: null } : l,
			);
			delete collapsedGroupsState.groups[group.id];
		});
		processQueue();
	};

	const toggleGroup = (groupId: string) => {
		collapsedGroupsState.groups[groupId] =
			!collapsedGroupsState.groups[groupId];
	};

	if (isMobile && sortedLists.length === 0 && sortedGroups.length === 0) {
		return (
			<div className={"flex justify-center items-center flex-1"}>
				<div
					className={
						"bg-secondary rounded text-xl p-4 text-opacity-60 cursor-pointer"
					}
					onClick={() => (uiState.newList = true)}
				>
					Get started by creating a list. <br />
					Tap here or the + at the top.
				</div>
			</div>
		);
	}

	const renderList = (id: string, nested: boolean) => {
		const list = listsById.get(id);
		if (!list) return null;
		return (
			<SidebarListView
				key={id}
				list={list}
				didDragRef={didDragRef}
				selected={currentList?.id === id}
				nested={nested}
			/>
		);
	};

	return (
		<SidebarGroup>
			{!isMobile && (
				<SidebarGroupLabel>
					<div
						className={
							"flex justify-between w-full items-center"
						}
					>
						<div>Lists</div>
						<AddMenu size={20} />
					</div>
				</SidebarGroupLabel>
			)}
			<SidebarGroupContent>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragStart}
					onDragOver={handleDragOver}
					onDragCancel={() => {
						didDragRef.current = true;
						setDragItems(null);
					}}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={items[UNGROUPED] ?? []}
						strategy={verticalListSortingStrategy}
					>
						<Container
							id={UNGROUPED}
							empty={(items[UNGROUPED] ?? []).length === 0}
						>
							{(items[UNGROUPED] ?? []).map((id) =>
								renderList(id, false),
							)}
						</Container>
					</SortableContext>

					{sortedGroups.map((group) => {
						const isCollapsed = !!collapsed[group.id];
						const ids = items[group.id] ?? [];
						return (
							<div key={group.id} className={"mt-2"}>
								<GroupHeader
									group={group}
									collapsed={isCollapsed}
									onToggle={() =>
										toggleGroup(group.id)
									}
									onEdit={() =>
										(uiState.editingGroup =
											group)
									}
									onDelete={() =>
										handleDeleteGroup(group)
									}
								/>
								{!isCollapsed && (
									<SortableContext
										items={ids}
										strategy={
											verticalListSortingStrategy
										}
									>
										<Container
											id={group.id}
											empty={ids.length === 0}
										>
											{ids.map((id) =>
												renderList(
													id,
													true,
												),
											)}
										</Container>
									</SortableContext>
								)}
							</div>
						);
					})}
				</DndContext>
			</SidebarGroupContent>
		</SidebarGroup>
	);
};

export { SidebarLists };
